import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import OutlineCard from "../../../../../../components/OutlineCard";
import Text from "../../../../../../components/Text";
import Flex from "../../../../../../components/Flex";
import { DATE_FORMAT } from "../../../../../../utils/date-time-utility";

interface PatientPhysicianPatientArbitrationAgreementProps {
  values: any;
}

const arbitrationSections = [
  {
    title: "Article 1: Agreement to Arbitrate:",
    content:
      "It is understood that any dispute as to medical malpractice, that is as to whether any medical services rendered under this contract were unnecessary or unauthorized or were improperly, negligently, or incompetently rendered, will be determined by submission to arbitration as provided by California law, and not by a lawsuit or resort to court process except as California law provides for judicial review or arbitration proceedings. Both parties to this contract, by entering into it, are giving up their constitutional rights to have any such dispute decided in a court of law before a jury, and instead are accepting the use of arbitration.",
  },
  {
    title: "Article 2: All Claims Must be Arbitrated:",
    content:
      "It is the intention of the parties that this agreement bind all parties whose claims may arise out of or related to treatment or service provided by the physician including any spouse or heirs of the patient and any children, whether born or unborn, at the time of the occurrence giving rise to any claim. In the case of any pregnant mother, the term 'patient' herein shall mean the mother and the mother's expected child or children.\n\nAll claims for monetary damages exceeding the jurisdictional limit of the small claims court against the physician, and the physician's partners, associates, association, corporation or partnership, and the employees, agents and estates of any of them, must be arbitrated including, without limitation, claims for loss of consortium, wrongful death, emotional distress or punitive damages. Filing of any court by the physician to collect any fee from the patient shall not waive the right to compel arbitration of any malpractice claim.",
  },
  {
    title: "Article 3: Procedures and Applicable Law:",
    content:
      "A demand for arbitration must communicate in writing to all parties. Each party shall select an arbitrator (party arbitrator) within thirty days and a third arbitrator (neutral arbitrator) shall be selected by the arbitrators appointed by the parties within thirty days of a demand for a neutral arbitrator by either party. Each party to the arbitration shall pay such party's pro rata share of the expenses and fees of the neutral arbitrator, together with other expenses of the arbitration incurred or approved by the neutral arbitrator, not including counsel fees or witness fees, or other expenses incurred by a party for such party's own benefit. The parties agree that the arbitrators have the immunity of a judicial officer from civil liability when acting in the capacity of arbitrator under this contract. This immunity shall supplement, not supplant, any other applicable statutory or common law.\n\nEither party shall have the absolute right to arbitrate separately the issues of liability and damages upon written request to the neutral arbitrator.\n\nThe parties consent to the intervention and joinder in this arbitration of any person or entity which would otherwise be a proper additional party in a court action, and upon such intervention and joinder any existing court action against such additional person or entity shall be stayed pending arbitration.\n\nThe parties agree that the provisions of California law applicable to health care providers shall apply to disputes within this arbitration agreement, including, but not limited to, Code of Civil Procedure Section 340.5 and 667.7 and Civil Code Sections 3333.1 and 3333.2. Any party may bring before the arbitrations a motion for summary judgment or summary adjudication in accordance with the Code of Civil Procedure. Discovery shall be conducted pursuant to Code of Civil Procedure section 1283.05, however, depositions may be taken without prior approval of the neutral arbitrator.",
  },
  {
    title: "Article 4: General Provisions:",
    content:
      "All claims based upon the same incident, transaction or related circumstances shall be arbitrated in one proceeding. A claim shall be waived and forever barred if (1) on the date notice thereof is received, the claim, if asserted in a civil action, would be barred by the applicable California statute of limitations, or (2) the claimant fails to pursue the arbitration claim in accordance with the procedures prescribed herein with reasonable diligence. With respect to any matter not herein expressly provided for, the arbitrators shall be governed by the California Code of Civil Procedure provisions relating to arbitration.",
  },
  {
    title: "Article 5: Revocation:",
    content:
      "This agreement may be revoked by written notice delivered to the physician within 30 days, or signature. It is the intent of this agreement to apply to all medical services rendered any time for any condition.",
  },
  {
    title: "Article 6: Retroactive Effect:",
    content:
      "If patient intends this agreement to cover services rendered before the date it is Effective as of the date of first medical services.",
  },
];

const PatientPhysicianPatientArbitrationAgreement = ({
  values,
}: PatientPhysicianPatientArbitrationAgreementProps) => {
  // Format date
  const formatDate = (date: any) => {
    if (!date) return "";
    try {
      return dayjs(date).format(DATE_FORMAT);
    } catch {
      return "";
    }
  };

  return (
    <Box mt={1} id={"Physician Patient Arbitration Agreement"}>
      <OutlineCard>
        <Box p={2}>
          {/* Title */}
          <Typography
            variant="h5"
            align="center"
            fontWeight="bold"
            gutterBottom
          >
            Physician Patient Arbitration Agreement
          </Typography>

          {/* Arbitration Sections */}
          {arbitrationSections.map((section, idx) => (
            <Box key={idx} mb={2}>
              <Text variant="subtitle1" fontWeight="bold" gutterBottom>
                {section.title}
              </Text>
              <Text variant="body2" style={{ whiteSpace: "pre-line" }}>
                {section.content}
              </Text>
            </Box>
          ))}

          {/* Additional Text */}
          <Box mb={2}>
            <Text variant="body2">
              If any provision of this arbitration agreement is held invalid or
              unenforceable, the remaining provisions shall remain in full force
              and shall not be affected by the invalidity of any other
              provision.
            </Text>
            <Text variant="body2" mt={1}>
              I understand that I have the right to receive a copy of this
              arbitration agreement. By my signature below, I acknowledge that I
              have received a copy.
            </Text>
          </Box>

          {/* Notice Section */}
          <Box mb={2}>
            <Text variant="body2" fontWeight="bold">
              NOTICE: BY SIGNING THIS CONTRACT YOU ARE AGREEING TO HAVE ANY
              ISSUE OF MEDICAL MALPRACTICE DECIDED BY NEUTRAL ARBITRATION AND
              YOU ARE GIVING UP YOUR RIGHT TO A JURY OR COURT TRIAL. SEE ARTICLE
              1 OF THIS CONTRACT.
            </Text>
          </Box>

          {/* Date */}

          <Flex>
            <Text>
              Date :{" "}
              {values.physicianPatientArbitrationAgreementDate
                ? formatDate(values.physicianPatientArbitrationAgreementDate)
                : ""}
            </Text>
          </Flex>
          {/* Signature Section */}
          {values.physicianPatientArbitrationAgreementSign && (
            <Box mt={2}>
              <Text mb={1} fontWeight="medium">
                Signature
              </Text>
              <img
                src={values.physicianPatientArbitrationAgreementSign}
                alt="Physician Patient Arbitration Agreement Signature"
                style={{
                  height: "150px",
                  width: "360px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </Box>
          )}
        </Box>
      </OutlineCard>
    </Box>
  );
};

export default PatientPhysicianPatientArbitrationAgreement;
